import {
  addTokenToHeaders,
  addCirxModuleToHeaders,
  Api,
  removeCirxModuleFromHeaders,
} from "@/core/services/api.service";
import { getCurrentDomain, isDashboardApp } from "@/core/services/domain.service";
import { removeUserBodyClass } from "@/core/services/bodyModificator";
import { resetCustomLayoutOnLogout, setCustomLayoutByOrganization } from "@/core/services/layout.service";
import { ORDservice } from "@/core/services/identification/organizationRootDetails.service";
import routerDashboard from "@/routing/routerDashboard";
import { routes } from "@/routing/routes";
import useUserStore from "@/apps/shared/user/store/userStore";
import { UserAdapter } from "@/core/models/user";
import { premiumFeaturesFromConnectorService } from "./api/premiumFeaturesFromConnector.service";
import typesenseClient from "@/core/services/typesense/typesenseClient.service";
import useCategoryStore from "@/core/store/catalog/categoryStore";
import useAccountStore from "@/apps/shared/organization/store/accountStore";
import i18nService from "@/core/composables/i18n.service";
import { FR_LANGUAGE } from "@/core/composables/vue-i18n";
import useOrganizationRootDetailsStore from "@/core/store/organizationRootDetailsStore";
import { useCartStore } from "@/core/store/cart/cartStore";

const loginService = {
  OPENID_LOGOUT_URL_CACHE_KEY: "open_id_logout_url",

  /**
   * @param {string} email
   * @param {string} password
   */
  async passwordLogin(email, password) {
    const response = await Api.new().post("api/login", {
      email: email,
      password: password,
    });

    return this.successfullLoginCallback(response);
  },

  async openIdLogin(organizationId, openidSessionId) {
    const response = await Api.new().post("api/login", {
      organizationId: organizationId,
      openidSessionId: openidSessionId,
    });

    return this.successfullLoginCallback(response);
  },

  async successfullLoginCallback(response) {
    const { clearCategoryTree } = useCategoryStore();
    const isDashboard = isDashboardApp();

    clearCategoryTree();

    const { setUser } = useUserStore();

    const user = UserAdapter.adaptAuthenticatedUser(response.data);
    setUser(user);

    addCirxModuleToHeaders(user.getCurrentModule());
    addTokenToHeaders(user.apiToken);

    if (user.currentOrganization) {
      useAccountStore().initFromAccount(user.currentOrganization.account);
      const ord = await ORDservice.fetchORDByOrganizationId(user.currentOrganization.account.id);
      useOrganizationRootDetailsStore().setFromOrdApi(ord);
      setCustomLayoutByOrganization(true);
      await premiumFeaturesFromConnectorService.fetchAndSaveEnabledConnectorPremiumFeatures();
      await useCartStore().initCart(isDashboard);
    } else {
      // Force FR language for Admin & Superadmin
      i18nService.setActiveLanguage(FR_LANGUAGE);
    }
  },

  saveOpenIdLogoutUrl(logoutUrl) {
    localStorage.setItem(this.OPENID_LOGOUT_URL_CACHE_KEY, logoutUrl);
  },

  /**
   * @param {string} queryString
   */
  openIdLogout(queryString) {
    if (!this.isConnectedUsingOpenId()) {
      return;
    }
    let loginUrl = `${getCurrentDomain()}/login`;
    if (queryString) {
      loginUrl += queryString;
    }
    const logoutUrl = new URL(localStorage.getItem(this.OPENID_LOGOUT_URL_CACHE_KEY));
    logoutUrl.searchParams.set("post_logout_redirect_uri", loginUrl);
    localStorage.removeItem(this.OPENID_LOGOUT_URL_CACHE_KEY);
    window.location.href = logoutUrl;
  },

  isConnectedUsingOpenId() {
    return localStorage.getItem(this.OPENID_LOGOUT_URL_CACHE_KEY) !== null;
  },

  logout() {
    const { resetUser } = useUserStore();
    resetUser();

    removeCirxModuleFromHeaders();
    removeUserBodyClass();
    resetCustomLayoutOnLogout();
    typesenseClient.clearToken();
    useOrganizationRootDetailsStore().reset();
    useAccountStore().reset();
    premiumFeaturesFromConnectorService.removeEnabledConnectorPremiumFeaturesLocally();

    const { clearCategoryTree } = useCategoryStore();
    clearCategoryTree();

    if (this.isConnectedUsingOpenId()) {
      this.openIdLogout();
    } else {
      routerDashboard.push(routes.login.path);
    }
  },

  async getOpenIdAuthorizationUrl(domain) {
    const response = await Api.new().get(`/open-id/authorization-url?domain=${domain}`);

    return response.data.url;
  },
};

export default loginService;
